import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Key, Plus, Trash2, ArrowLeft, Copy, Check, Package, Search
} from 'lucide-react';

export default function SerialKeys() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Selected product state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [keyData, setKeyData] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [newKeys, setNewKeys] = useState('');
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(null);

  document.title = 'Serial Key — Tokomoe';

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/products', { params: { type: 'digital', limit: 200 } });
      setProducts(data.products || []);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const fetchSerialKeys = async (productId) => {
    setLoadingKeys(true);
    try {
      const { data } = await api.get(`/tenant/products/${productId}/serial-keys`);
      setKeyData(data);
    } catch (err) {
      console.error('Failed to load keys:', err);
    } finally {
      setLoadingKeys(false);
    }
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setNewKeys('');
    fetchSerialKeys(product.id);
  };

  const addKeys = async () => {
    if (!newKeys.trim()) return;
    setAdding(true);
    try {
      const keys = newKeys.split('\n').map(k => k.trim()).filter(Boolean);
      const { data } = await api.post(`/tenant/products/${selectedProduct.id}/serial-keys`, { keys });
      setNewKeys('');
      fetchSerialKeys(selectedProduct.id);
      alert(`${data.added} key ditambahkan${data.duplicates_skipped ? `, ${data.duplicates_skipped} duplikat dilewati` : ''}`);
    } catch (err) {
      console.error('Failed to add keys:', err);
    } finally {
      setAdding(false);
    }
  };

  const deleteKey = async (key) => {
    if (!window.confirm(`Hapus serial key "${key}"?`)) return;
    try {
      await api.delete(`/tenant/products/${selectedProduct.id}/serial-keys/${encodeURIComponent(key)}`);
      fetchSerialKeys(selectedProduct.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Gagal menghapus key');
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const filteredProducts = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  // Selected product detail view
  if (selectedProduct) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedProduct(null); setKeyData(null); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Kembali ke daftar produk
        </Button>

        <div>
          <h1 className="text-2xl font-bold text-slate-900">{selectedProduct.name}</h1>
          <p className="text-slate-500">Kelola serial key untuk produk ini</p>
        </div>

        {/* Stats */}
        {keyData && (
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-slate-500">Total Key</p>
                <p className="text-2xl font-bold">{keyData.total}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-slate-500">Tersedia</p>
                <p className="text-2xl font-bold text-green-600">{keyData.available}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-slate-500">Terjual</p>
                <p className="text-2xl font-bold text-blue-600">{keyData.assigned}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Keys */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4" /> Tambah Serial Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea rows={5} placeholder="Masukkan serial key, satu per baris. Contoh:&#10;XXXX-YYYY-ZZZZ-1111&#10;XXXX-YYYY-ZZZZ-2222&#10;XXXX-YYYY-ZZZZ-3333"
              value={newKeys} onChange={e => setNewKeys(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">{newKeys.split('\n').filter(k => k.trim()).length} key siap ditambahkan</p>
              <Button size="sm" onClick={addKeys} disabled={adding || !newKeys.trim()}>
                <Plus className="w-4 h-4 mr-1" /> {adding ? 'Menambahkan...' : 'Tambah Key'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Key List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Daftar Serial Key</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingKeys ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
            ) : !keyData?.keys?.length ? (
              <div className="text-center py-8 text-slate-500">
                <Key className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>Belum ada serial key</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">#</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Serial Key</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {keyData.keys.map((key, i) => {
                      const assignedEntry = keyData.assigned_keys?.find(a => a.serial_key === key);
                      const isAssigned = !!assignedEntry;
                      return (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                          <td className="px-4 py-3 font-mono text-slate-900">{key}</td>
                          <td className="px-4 py-3">
                            {isAssigned ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Terjual</span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Tersedia</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => copyKey(key)} title="Salin">
                                {copied === key ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-slate-400" />}
                              </Button>
                              {!isAssigned && (
                                <Button variant="ghost" size="sm" onClick={() => deleteKey(key)} title="Hapus">
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Product List View
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Serial Key</h1>
        <p className="text-slate-500">Kelola serial key untuk produk digital</p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" placeholder="Cari produk digital..." value={search} onChange={e => setSearch(e.target.value)}
          className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
      </div>

      {/* Product List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Tidak ada produk digital ditemukan</p>
            <p className="text-xs mt-1">Buat produk dengan tipe "Digital" terlebih dahulu</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map(p => (
            <Card key={p.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => selectProduct(p)}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Key className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-900 truncate">{p.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Stok: {p.serial_keys?.length || p.stock || 0} · Rp {(p.price || 0).toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
